import { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  Tag,
} from "antd";
import styled from "styled-components";
import { FaSave, FaInfoCircle } from "react-icons/fa";
import { MdLink, MdEdit } from "react-icons/md";
import { showSuccess, showError } from "infra/helpers/alerta";
import useCreateClasse from "../../hooks/useCreateClasse";
import useUpdateClasse from "../../hooks/useUpdateClasse";
import useGetClasseById from "../../hooks/useGetClasseById";
import useGetOperacoes from "../../hooks/useGetOperacoes";
import useGetOperacoesByClasseId from "../../hooks/useGetOperacoesByClasseId";
import useModal from "../../hooks/useModal";

const { Item, useForm } = Form;

interface CreateEditModalProps {
  refetch: () => void;
}

function CreateEditModal({ refetch }: CreateEditModalProps) {

  const [operacoesIds, setOperacoesIds] = useState<string[]>([]);
  const [operacoesRemovidasIds, setOperacoesRemovidasIds] = useState<string[]>([]);
  const [operacoesAdicionadasIds, setOperacoesAdicionadasIds] = useState<string[]>([]);
  const [operacoesTags, setOperacoesTags] = useState<any[]>([]);

  const [form] = useForm();
  const {
    id,
    isOpen,
    isEditing,
    setId,
    setIsOpen,
    setIsEditing,
  } = useModal();

  const { mutateAsync: createClasse, isLoading: loadingCreate } = useCreateClasse();
  const { mutateAsync: updateClasse, isLoading: loadingUpdate } = useUpdateClasse();

  const { data } = useGetClasseById(id!, isOpen);
  const { data: operacoesClasse } = useGetOperacoesByClasseId(data?.operacaoClasseId);

  const { data: operacoes, isLoading: loadingOperacoes } = useGetOperacoes(isOpen);
  const operacoesOptions = operacoes
    ?.sort((a, b) => a.codigo - b.codigo)
    ?.map(item => ({ value: item.operacaoId, label: `${item.codigo} - ${item.descricao}`, disabled: item.operacaoClasseId && item.operacaoClasseId !== data?.operacaoClasseId }));

  async function handleCreate() {
    const body = form.getFieldsValue();
    const response = await createClasse({ ...body, operacoesIds, cor: body?.cor ? body?.cor : "#000000" });
    if (response.status === 201) {
      showSuccess("Classe de Operação cadastrada com sucesso!");
      handleClose();
      refetch();
      return;
    }
    const details = (response as any).response.data.Details;
    const error = details[0].Errors[0];
    showError(error);
    handleClose();
  }

  async function handleUpdate() {
    const body = form.getFieldsValue();
    const response = await updateClasse({
      ...body,
      id,
      cor: body?.cor ? body?.cor : "#000000",
      operacoesIds: operacoesAdicionadasIds,
      operacoesRemovidasIds,
    });
    if (response.status === 204) {
      showSuccess("Classe de Operação editada com sucesso!");
      handleClose();
      refetch();
      return;
    }
    const details = (response as any).response.data.Details;
    const error = details[0].Errors[0];
    showError(error);
    handleClose();
  }

  function handleSelectOperacoes(operacoesIds: string[], fromTag?: boolean) {
    if (fromTag) {
      setOperacoesRemovidasIds(prev => [...prev, ...operacoesIds]);
      setOperacoesAdicionadasIds(prev => prev.filter(item => !operacoesIds.includes(item)));
      setOperacoesTags(operacoesTags.filter(item => !operacoesIds.includes(item.operacaoId)));
      return;
    }

    if (isEditing) {
      const operacoesClasseIds = operacoesClasse?.map(item => item.operacaoId) ?? [];

      const adicionados = operacoesIds.filter(item => !operacoesClasseIds?.includes(item)) ?? [];
      setOperacoesAdicionadasIds(adicionados);

      const removidos = operacoesClasseIds?.filter(item => !operacoesIds.includes(item)) ?? [];
      setOperacoesRemovidasIds(removidos);

      const novosRemovidos = operacoesAdicionadasIds.filter(item => !operacoesIds.includes(item));
      const retornados = operacoesIds?.filter(item => operacoesRemovidasIds.includes(item) && operacoesClasseIds.includes(item));

      const operacoesFiltradas = operacoesTags.filter(item => !removidos.includes(item.operacaoId) && !novosRemovidos.includes(item.operacaoId)) ?? [];
      const operacoesAdicionadas = operacoes?.filter(item => adicionados.includes(item.operacaoId)) ?? [];
      const operacoesRetornadas = operacoes?.filter(item => retornados.includes(item.operacaoId)) ?? [];

      const resultado = Array.from(new Set([...operacoesFiltradas, ...operacoesAdicionadas, ...operacoesRetornadas]));
      setOperacoesTags(resultado);

      return;
    }

    const tags = operacoes?.filter(item => operacoesIds.includes(item.operacaoId)) ?? [];
    setOperacoesIds(operacoesIds);
    setOperacoesTags(tags);
  }

  function handleClose() {
    setIsEditing(false);
    setIsOpen(false);
    setId(null);
    setOperacoesTags([]);
    setOperacoesAdicionadasIds([]);
    setOperacoesRemovidasIds([]);
    setOperacoesIds([]);
    form.resetFields();
  }

  const statusOptions = [
    { value: true, label: "Ativo" },
    { value: false, label: "Inativo" },
  ];

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isEditing && operacoesClasse) {
      setOperacoesTags(operacoesClasse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operacoesClasse]);

  return (
    <CustomModal
      title={
        <Header>
          <MdEdit size={18} />
          {isEditing ? "Editar" : "Nova"} Classe de Operação
        </Header>
      }
      visible={isOpen}
      width={700}
      onCancel={handleClose}
      maskClosable={false}
      footer={
        <>
          <Button
            size="large"
            loading={loadingCreate || loadingUpdate}
            onClick={handleClose}
            style={{ borderRadius: 8 }}
          >Cancelar</Button>
          <Button
            size="large"
            type="primary"
            loading={loadingCreate || loadingUpdate}
            onClick={isEditing ? handleUpdate : handleCreate}
            style={{ borderRadius: 8, display: "flex", alignItems: "center", gap: 8, float: "right", backgroundColor: "#12619C" }}
            icon={<FaSave />}
          >Salvar Classe</Button>
        </>
      }
    >
      <Title>
        <FaInfoCircle size={18} /> Informações Básicas
      </Title>
      <Form
        form={form}
        layout="vertical"
      >
        <Row gutter={[16, 16]}>
          <Col span={4}>
            <Item label="Código" name="codigo">
              <Input
                size="large"
                placeholder="Código"
                disabled
                style={{ borderRadius: 8 }}
              />
            </Item>
          </Col>

          <Col span={20}>
            <Item label="Descrição" name="descricao">
              <Input
                size="large"
                placeholder="Descrição"
                style={{ borderRadius: 8 }}
              />
            </Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={4}>
            <Item label="Cor" name="cor">
              <StyledColorInput
                type="color"
                defaultValue="#000000"
              />
            </Item>
          </Col>

          <Col span={8}>
            <Item label="Status" name="ativo">
              <StyledSelect
                size="large"
                defaultValue={true}
                options={statusOptions}
                placeholder="Status"
              />
            </Item>
          </Col>
        </Row>
      </Form>

      <Title>
        <MdLink size={22} /> Vincular Operações do GeoAgrícola
      </Title>
      <StyledSelect
        showSearch
        size="large"
        mode="multiple"
        optionFilterProp="label"
        autoClearSearchValue={false}
        loading={loadingOperacoes}
        maxTagCount={0}
        maxTagPlaceholder={(values) => `${values.length} operações`}
        options={operacoesOptions}
        placeholder="Operações do GeoAgrícola"
        value={operacoesTags.map(item => item.operacaoId)}
        onChange={value => handleSelectOperacoes(value as string[])}
        style={{ width: "100%" }}
      />

      <Tags>
        {operacoesTags.map(item => (
          <Tag
            color="processing"
            style={{ cursor: "pointer", margin: 0 }}
            onClick={() => handleSelectOperacoes([item.operacaoId], true)}
          >
            {item.descricao} <b>X</b>
          </Tag>
        ))}
      </Tags>
    </CustomModal>
  );
}

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h2`
  margin-bottom: 8px;
  font-weight: bold;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const StyledColorInput = styled.input`
  width: 40px;
  height: 40px;
  border: none;
  outline: none;
  background: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
    border-radius: 10px;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 10px;
  }

  &::-moz-color-swatch {
    border: none;
    border-radius: 10px;
  }
`;

const CustomModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 10px;
  }

  .ant-modal-header {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    background-color: #12619C;
  }

  .ant-modal-title {
    color: #fff;
    font-weight: bold;
  }

  .ant-modal-close-x {
    color: #fff;
  }
`;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 8px !important;
  }
`;

export default CreateEditModal;
